import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAdminTeacher,
  fetchTeacherDetails,
} from "../../../redux/Actions/teacherAdminActions";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  Typography,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import { Link } from "react-router-dom"; // To link to TeacherInformation page
import AdminNavbar from "./AdminNavbar"; // Reusing the AdminNavbar component
import "./AdminDashboardLayout.css"; // Import specific admin styles

const TeacherAdminPage = () => {
  const dispatch = useDispatch();
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createForm, setCreateForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    dateOfBirth: "",
    job: "",
    nationality: "",
    accommodation: "",
    phoneNumber: "",
    displayOrder: "",
    bio: "",
    bioAr: "",
    bioDe: "",
    study: "",
    studyAr: "",
    studyDe: "",
    experience: "",
    experienceAr: "",
    experienceDe: "",
    specialization: "",
    specializationAr: "",
    specializationDe: "",
    academicQualifications: "",
    academicQualificationsAr: "",
    academicQualificationsDe: "",
    courseDescription: "",
    courseDescriptionAr: "",
    courseDescriptionDe: "",
    confirmedFromAdmin: true,
    status: "approved",
  });

  // Fetch teacher data on component mount
  useEffect(() => {
    dispatch(fetchTeacherDetails());
  }, [dispatch]);

  // Get teacher data and loading status from the Redux store
  const { teachers, loading } = useSelector(
    (state) => state.teacherAdmin
  );
  const { saving } = useSelector((state) => state.teacherDetails);

  const handleCreateField = (event) => {
    const { name, value, type, checked } = event.target;
    setCreateForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreateTeacher = async () => {
    setCreateError("");
    try {
      const payload = {
        ...createForm,
        dateOfBirth: createForm.dateOfBirth || null,
        displayOrder: createForm.displayOrder === "" ? null : Number(createForm.displayOrder),
        status: createForm.confirmedFromAdmin ? "approved" : "pending",
      };
      await dispatch(createAdminTeacher(payload));
      setCreateOpen(false);
    } catch (error) {
      setCreateError(
        error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          "Failed to create teacher"
      );
    }
  };

  if (loading) {
    return <CircularProgress />; // Show a loader when data is being fetched
  }

  return (
    <div className="admin-dashboard-layout">
      <AdminNavbar /> {/* Reusing the AdminNavbar */}
      <Container className="admin-dashboard-content">
        <Box sx={{ my: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 2 }}>
            <Typography variant="h4" gutterBottom>
              Teacher Management
            </Typography>
            <Button
              variant="contained"
              onClick={() => setCreateOpen(true)}
              sx={{
                alignSelf: "center",
                textTransform: "none",
                backgroundColor: "#FFD600",
                color: "#111",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#e6ac00" },
              }}
            >
              Create Teacher
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Full Name</TableCell>{" "}
                  {/* Full Name column */}
                  <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Date of Birth
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Job</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Nationality</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Accommodation
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Phone Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Qualifications
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Team Order</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Profile</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teachers.map((teacher) => (
                  <TableRow key={teacher.teacherId}>
                    <TableCell>
                      <Link
                        to={`/dashboard/teacher/${teacher.teacherId}/details`}
                        style={{ textDecoration: "none", color: "blue" }}
                      >
                        {`${teacher.firstName} ${teacher.lastName}` || "NA"}
                      </Link>{" "}
                      {/* Full name linked */}
                    </TableCell>
                    <TableCell>{teacher.email || "NA"}</TableCell>
                    <TableCell>
                      {teacher.dateOfBirth
                        ? new Date(teacher.dateOfBirth).toLocaleDateString()
                        : "NA"}
                    </TableCell>
                    <TableCell>{teacher.job || "NA"}</TableCell>
                    <TableCell>{teacher.nationality || "NA"}</TableCell>
                    <TableCell>{teacher.accommodation || "NA"}</TableCell>
                    <TableCell>{teacher.phoneNumber || "NA"}</TableCell>
                    <TableCell>
                      {teacher.qualifications &&
                      teacher.qualifications.length > 0
                        ? teacher.qualifications.join(", ")
                        : "NA"}
                    </TableCell>
                    <TableCell>{teacher.displayOrder ?? "Last"}</TableCell>
                    <TableCell>
                      <Button
                        component={Link}
                        to={`/dashboard/teacher/${teacher.teacherId}/details`}
                        variant="contained"
                        size="small"
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#FFD600",
                          color: "#111",
                          fontWeight: 700,
                          "&:hover": { backgroundColor: "#e6ac00" },
                        }}
                      >
                        View / Edit Profile
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Container>
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Teacher</DialogTitle>
        <DialogContent dividers>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {[
              ["firstName", "First Name"],
              ["lastName", "Last Name"],
              ["email", "Email"],
              ["password", "Temporary Password"],
              ["dateOfBirth", "Date of Birth"],
              ["job", "Job"],
              ["nationality", "Nationality"],
              ["accommodation", "Accommodation"],
              ["phoneNumber", "Phone Number"],
              ["displayOrder", "Team Order"],
              ["study", "Study (EN/default)"],
              ["experience", "Experience (EN/default)"],
              ["specialization", "Specialization (EN/default)"],
              ["studyDe", "Study (DE)"],
              ["experienceDe", "Experience (DE)"],
              ["specializationDe", "Specialization (DE)"],
            ].map(([name, label]) => (
              <Grid item xs={12} sm={6} key={name}>
                <TextField
                  fullWidth
                  required={["firstName", "lastName", "email", "password"].includes(name)}
                  name={name}
                  label={label}
                  type={
                    name === "password"
                      ? "password"
                      : name === "dateOfBirth"
                      ? "date"
                      : name === "displayOrder"
                      ? "number"
                      : "text"
                  }
                  value={createForm[name]}
                  onChange={handleCreateField}
                  InputLabelProps={name === "dateOfBirth" ? { shrink: true } : undefined}
                />
              </Grid>
            ))}
            {[
              ["studyAr", "Study (AR)"],
              ["experienceAr", "Experience (AR)"],
              ["specializationAr", "Specialization (AR)"],
            ].map(([name, label]) => (
              <Grid item xs={12} sm={6} key={name}>
                <TextField
                  fullWidth
                  name={name}
                  label={label}
                  value={createForm[name]}
                  onChange={handleCreateField}
                  inputProps={{ dir: "rtl" }}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                name="bio"
                label="Bio (EN/default)"
                value={createForm.bio}
                onChange={handleCreateField}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                name="bioAr"
                label="Bio (AR)"
                value={createForm.bioAr}
                onChange={handleCreateField}
                inputProps={{ dir: "rtl" }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                name="bioDe"
                label="Bio (DE)"
                value={createForm.bioDe}
                onChange={handleCreateField}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                name="academicQualifications"
                label="Academic Qualifications (EN/default)"
                value={createForm.academicQualifications}
                onChange={handleCreateField}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                name="courseDescription"
                label="My Courses (EN/default)"
                value={createForm.courseDescription}
                onChange={handleCreateField}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                name="academicQualificationsAr"
                label="Academic Qualifications (AR)"
                value={createForm.academicQualificationsAr}
                onChange={handleCreateField}
                inputProps={{ dir: "rtl" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                name="courseDescriptionAr"
                label="My Courses (AR)"
                value={createForm.courseDescriptionAr}
                onChange={handleCreateField}
                inputProps={{ dir: "rtl" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                name="academicQualificationsDe"
                label="Academic Qualifications (DE)"
                value={createForm.academicQualificationsDe}
                onChange={handleCreateField}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                name="courseDescriptionDe"
                label="My Courses (DE)"
                value={createForm.courseDescriptionDe}
                onChange={handleCreateField}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="confirmedFromAdmin"
                    checked={createForm.confirmedFromAdmin}
                    onChange={handleCreateField}
                  />
                }
                label="Confirm and publish teacher"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTeacher}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeacherAdminPage;
